import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import ModalSheet from 'LuxKit/Modal/ModalSheet'
import ModalBackdrop from 'LuxKit/Modal/ModalBackdrop'
import ScrollLock from 'components/Common/ScrollLock'
import AccountAccessModalContent from './AccountAccessModalContent'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { modalClose, setAccountAccessMode } from 'actions/UiActions'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import type { ModalElementContextValue } from 'contexts/ModalProvider'
import { ModalElementContext } from 'contexts/ModalProvider'
import noop from 'lib/function/noop'
import { trackClientEvent } from 'analytics/analytics'
import { getAccessModalAction } from 'lib/analytics/getAccessModalAction'
import { MasterModalRegistrationContext } from 'contexts/MasterModalContext'
import { stopPropagation } from 'lib/web/eventHandlerUtils'

interface Props {
  utm: App.UTMState
  modal: App.UiModalState
  modalMode: App.UiAccountModalMode
}

function AccountAccessSSRModal(props: Props) {
  const {
    modalMode,
    modal,
    utm,
  } = props

  const dispatch = useAppDispatch()
  const open = modal.open && modal.modalType === 'accountAccess'
  const loading = useAppSelector(state => state.auth.processing)
  const dismissable = utm.join !== '2' && modal.dismissable
  const isSecondChanceMode = modalMode === 'promptAddPhone' || modalMode === 'promptAddPhoneOnboarding' || modalMode === 'joinOTP'
  const [secondChanceDismissed, setSecondChanceDismissed] = useState(false)

  const { registerModal, unregisterModal } = useContext(MasterModalRegistrationContext)

  const onModeChange = useCallback((mode: App.UiAccountModalMode) => {
    dispatch(setAccountAccessMode(mode))
  }, [dispatch])

  const closeModal = useCallback(() => {
    if (!dismissable) return
    const action = getAccessModalAction(modalMode)
    if (action) {
      trackClientEvent({
        subject: 'sign_up_dismiss',
        action,
        category: 'logging',
        type: 'operational',
      })
    }
    dispatch(modalClose())
  }, [dismissable, dispatch, modalMode])

  const contextValue = useMemo((): ModalElementContextValue<any> => ({
    open,
    afterClose: noop,
    resolve: closeModal,
  }), [closeModal, open])

  const handleBackdropClick = useCallback(() => {
    if (!dismissable) return
    if (isSecondChanceMode && !secondChanceDismissed) {
      setSecondChanceDismissed(true)
      return
    }
    closeModal()
  }, [dismissable, isSecondChanceMode, secondChanceDismissed, closeModal])
  const handleModalClick = useCallback(stopPropagation, [])

  useEffect(() => {
    if (!open || !isSecondChanceMode) {
      setSecondChanceDismissed(false)
    }
  }, [open, isSecondChanceMode])

  useEffect(() => {
    if (open) {
      const localId = registerModal('ssr-account-access')

      return () => {
        // always unregister our modal on unmount
        if (localId) {
          unregisterModal(localId)
        }
      }
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  return (
    <>
      <ScrollLock isActive={open} />
      {open &&
        <ModalElementContext.Provider value={contextValue}>
          <ModalBackdrop mode="default" onClick={handleBackdropClick}>
            <ModalSheet
              mode="default"
              height="auto"
              tabletHeight="auto"
              size="S"
              onClick={handleModalClick}
            >
              <LoadingIndicator visible={loading} floating />
              <AccountAccessModalContent
                mode={modalMode}
                dismissable={dismissable}
                onModeChange={onModeChange}
                secondChanceDismissed={secondChanceDismissed}
                onSecondChanceDismissedChange={setSecondChanceDismissed}
              />
            </ModalSheet>
          </ModalBackdrop>
        </ModalElementContext.Provider>}
    </>
  )
}

function mapStateToProps(state: App.State) {
  return {
    modalMode: state.ui.accountAccess.mode,
    modal: state.ui.modal,
    utm: state.utm,
  }
}

export default connect(mapStateToProps)(AccountAccessSSRModal)
